import type { InputHTMLAttributes, ReactNode, Ref, TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'
import { useFieldControl } from './fieldContext'
import { controlBase, controlBorder } from './controlStyles'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** `touch` is 48px tall for phone screens. */
  size?: 'md' | 'touch'
  leading?: ReactNode
  trailing?: ReactNode
  invalid?: boolean
  ref?: Ref<HTMLInputElement>
}

export function Input({
  size = 'md',
  leading,
  trailing,
  invalid,
  className,
  id,
  ref,
  ...rest
}: InputProps) {
  const field = useFieldControl()
  const isInvalid = invalid ?? field?.invalid ?? false
  const input = (
    <input
      ref={ref}
      id={id ?? field?.id}
      aria-describedby={field?.describedBy}
      aria-invalid={isInvalid || undefined}
      aria-required={field?.required || undefined}
      className={cn(
        controlBase,
        controlBorder(isInvalid),
        size === 'touch' ? 'h-12 px-3.5 text-[17px]' : 'h-10 px-3 text-[15px]',
        leading && 'pl-10',
        trailing && 'pr-12',
        className,
      )}
      {...rest}
    />
  )
  if (!leading && !trailing) return input
  return (
    <div className="relative">
      {leading && (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[18px] text-muted">
          {leading}
        </span>
      )}
      {input}
      {trailing && (
        <span className="absolute inset-y-0 right-3 flex items-center text-[14px] text-muted">
          {trailing}
        </span>
      )}
    </div>
  )
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
  ref?: Ref<HTMLTextAreaElement>
}

export function TextArea({ invalid, className, id, rows = 3, ref, ...rest }: TextAreaProps) {
  const field = useFieldControl()
  const isInvalid = invalid ?? field?.invalid ?? false
  return (
    <textarea
      ref={ref}
      id={id ?? field?.id}
      rows={rows}
      aria-describedby={field?.describedBy}
      aria-invalid={isInvalid || undefined}
      className={cn(controlBase, controlBorder(isInvalid), 'px-3 py-2 text-[15px]', className)}
      {...rest}
    />
  )
}
